import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Icons } from 'atoms'
import { HeadPage } from 'atoms/web/HeadPage'
import { HStack, VStack, View, Button } from 'native-base'
import Layouts from 'organisms/Layouts'
import { FlatList, RefreshControl } from 'react-native'
import useDebounce from 'src/hooks/useDebounce'

import OpportunityFilterModalDesktop from './Opportunity/Filters/OpportunityFilterModal.desktop'
import { OpportunityFilterSubmitParams } from './Opportunity/Filters/OpportunityFilterScreen.types'
import {
  ListTypesRef,
  ListTypesView,
  monthNames,
  OpportunitiesViewsV1,
  OpportunitiesViewsV2,
  OrganizationScreenOpportunityComponent,
  ListType
} from './OrganizationScreen.types'
import { useOrganizationScreen } from './useOrganizationScreen'

const OrganizationScreenOpportunityDesktop: OrganizationScreenOpportunityComponent = ({
  navigation,
  route
}) => {
  const [search, setSearch] = useState('')

  const debouncedSearch = useDebounce(search, 500)

  const {
    handleToggleActivitiesTypeView,
    currentSelectedDate,
    isLoading,
    ascOrDesc,
    viewTypes,
    viewType,
    isGoodsAndServices
    //@ts-ignore
  } = useOrganizationScreen({ navigation, route })

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)

  const [selectedFilterListType, setSelectedFilterListType] = useState<ListType>(() => {
    return (route?.params?.listType ?? (viewType.group as ListType) ?? ListType.ODC) as ListType
  })

  useEffect(() => {
    if (route?.params?.listType) {
      setSelectedFilterListType(route.params.listType as ListType)
    }
  }, [route?.params?.listType])

  const openOpportunityFilterModal = useCallback((listType: ListType) => {
    setSelectedFilterListType(listType)

    setIsFilterModalOpen(true)
  }, [])

  const closeOpportunityFilterModal = useCallback(() => {
    setIsFilterModalOpen(false)
  }, [])

  const handleApplyOpportunityFilters = useCallback(
    (filters: OpportunityFilterSubmitParams) => {
      const listTypeToPersist =
        filters.listType ??
        selectedFilterListType ??
        (route?.params?.listType as ListType) ??
        (viewType.group as ListType)

      navigation.setParams({
        ...(route.params ?? {}),
        ...filters,
        listType: listTypeToPersist
      })

      setSelectedFilterListType(listTypeToPersist)

      closeOpportunityFilterModal()
    },
    [closeOpportunityFilterModal, navigation, route.params, selectedFilterListType, viewType.group]
  )

  const currentViewRef = useRef<ListTypesRef>(null)

  const selectedOpportunityView = viewType.group

  const CurrentOpportunityView = isGoodsAndServices
    ? OpportunitiesViewsV1[selectedOpportunityView]
    : OpportunitiesViewsV2[selectedOpportunityView]

  const CurrentView = CurrentOpportunityView

  const keyExtractor = useCallback(
    (item: { component: React.ElementType; key: string }) => item.key,
    []
  )

  const data = [
    {
      component: CurrentView,
      key: '1'
    }
  ]

  const renderItem = useCallback(
    (
      { item }: { item: { component: React.FunctionComponent<ListTypesView>; key: string } },
      index: number
    ) => {
      const Component = item.component as
        | React.FunctionComponent<ListTypesView>
        | React.ForwardRefExoticComponent<ListTypesView & React.RefAttributes<ListTypesRef>>

      return (
        <View
          bg="background.base"
          maxW={isGoodsAndServices ? 'auto' : 1122}
          flex={1}
          minH={600}
          pb={30}
          key={`${index}-${item.key}`}>
          <Component
            ref={currentViewRef}
            isLoading={isLoading}
            ascOrDesc={ascOrDesc}
            //@ts-ignore
            route={route}
            //@ts-ignore
            navigation={navigation}
            handleSelect={handleToggleActivitiesTypeView}
            openOpportunityFilterModal={openOpportunityFilterModal}
            data={{
              date: currentSelectedDate
            }}
          />
        </View>
      )
    },
    [
      ascOrDesc,
      currentSelectedDate,
      handleToggleActivitiesTypeView,
      isLoading,
      navigation,
      openOpportunityFilterModal,
      isGoodsAndServices,
      route
    ]
  )

  useEffect(() => {
    if (currentViewRef.current) {
      currentViewRef.current.search(debouncedSearch)
    }
  }, [debouncedSearch, currentViewRef])

  return (
    <Layouts.InternalDesktop onSearch={setSearch} search={search}>
      <View h="full" flex={1} px={8}>
        <FlatList
          data={data}
          keyExtractor={keyExtractor}
          ListHeaderComponent={
            <View>
              <HeadPage
                title="Oportunidades"
                badge={monthNames[new Date().getMonth()]}
                pages={[
                  {
                    id: 1,
                    title: 'Minha organização',
                    link: 'minha-organizacao/oportunidades/5',
                    params: null
                  },
                  {
                    id: 2,
                    title: 'Oportunidades',
                    link: 'minha-organizacao/oportunidades/5',
                    params: null
                  }
                ]}
              />

              <VStack bg="background.base" borderTopRadius={32} mt={8} pb={2}>
                <>
                  <HStack bgColor="background.base" justifyContent="space-between" mt={2}>
                    <View
                      flexDir="row"
                      alignItems="center"
                      flexWrap="wrap"
                      w={isGoodsAndServices ? '100%' : 'auto'}
                      //  @ts-ignore
                      style={{ gap: '8px' }}>
                      {viewTypes.map((vision) => {
                        const IconComponent = Icons[vision.icon] as typeof Icons.Add

                        return (
                          <Button
                            key={vision.title}
                            //@ts-ignore
                            onPress={() => handleToggleActivitiesTypeView(vision)}
                            colorScheme={
                              viewType.title === vision.title ? 'primary-light' : 'white'
                            }
                            borderRadius="full"
                            px={6}
                            size="sm"
                            _text={{
                              fontSize: '16px'
                            }}
                            h={10}
                            rightIcon={
                              isGoodsAndServices ? undefined : (
                                <IconComponent
                                  color={
                                    viewType.title === vision.title ? 'primary.500' : 'gray.700'
                                  }
                                  size="18px"
                                />
                              )
                            }>
                            {vision.title}
                          </Button>
                        )
                      })}
                    </View>
                  </HStack>
                </>
              </VStack>
            </View>
          }
          showsHorizontalScrollIndicator={isGoodsAndServices}
          decelerationRate="normal"
          scrollEventThrottle={16}
          onEndReached={() => {
            if (currentViewRef.current) {
              currentViewRef.current?.fetchMore?.()
            }
          }}
          flexShrink={1}
          showsVerticalScrollIndicator
          refreshControl={
            <RefreshControl
              refreshing={currentViewRef.current?.refreshing ?? false}
              onRefresh={() => {
                currentViewRef.current?.refetch?.()
              }}
              tintColor="white"
            />
          }
          // @ts-ignore
          renderItem={renderItem}
        />
      </View>

      {isFilterModalOpen && (
        <OpportunityFilterModalDesktop
          navigation={navigation}
          route={route}
          onClose={closeOpportunityFilterModal}
          onSubmit={handleApplyOpportunityFilters}
          initialListType={selectedFilterListType}
        />
      )}
    </Layouts.InternalDesktop>
  )
}

export default OrganizationScreenOpportunityDesktop
